// The scroll boxes of rendered Markdown tables (`div.md-table`, written by
// logic/markdown): which of them scroll sideways, and where they stand.

/** Where a box of `clientWidth` showing `scrollWidth` of content stands at `scrollLeft`. */
export interface FrameScroll {
  /** The content is wider than the box. */
  scrolls: boolean;
  /** Content lies beyond the left edge. */
  before: boolean;
  /** Content lies beyond the right edge. */
  after: boolean;
}

export function frameScroll(scrollLeft: number, scrollWidth: number, clientWidth: number): FrameScroll {
  // A pixel of slack: both widths are rounded, and a zoomed page scrolls by fractions.
  const scrolls = scrollWidth > clientWidth + 1;
  const left = Math.abs(scrollLeft);
  return { scrolls, before: scrolls && left > 1, after: scrolls && left + clientWidth < scrollWidth - 1 };
}

function mark(frame: HTMLElement) {
  const { scrolls, before, after } = frameScroll(frame.scrollLeft, frame.scrollWidth, frame.clientWidth);
  if (scrolls) {
    frame.setAttribute('role', 'region');
    frame.setAttribute('aria-label', 'Table, scrolls sideways');
    frame.tabIndex = 0;
  } else {
    for (const attr of ['role', 'aria-label', 'tabindex']) frame.removeAttribute(attr);
  }
  const more = [before && 'before', after && 'after'].filter(Boolean).join(' ');
  if (more) frame.setAttribute('data-more', more);
  else frame.removeAttribute('data-more');
}

/**
 * Keeps every table frame under `root` marked for as long as the returned
 * function has not been called: a frame whose table is wider than it is a
 * focusable region named "Table, scrolls sideways" (the arrow keys scroll
 * it), and carries `data-more` with `before` and/or `after` for the sides
 * that have more to scroll to; a frame whose table fits has none of these
 * and takes no place in the tab order. Call it again after `root`'s content
 * is replaced.
 */
export function watchTableFrames(root: HTMLElement): () => void {
  const frames = [...root.querySelectorAll<HTMLElement>('.md-table')];
  if (!frames.length) return () => {};
  const onScroll = (e: Event) => mark(e.currentTarget as HTMLElement);
  // The frame's own size and its table's: either changes what fits.
  const sizes =
    typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver((entries) => {
          for (const frame of new Set(entries.map((e) => (e.target as Element).closest<HTMLElement>('.md-table')))) if (frame) mark(frame);
        });
  for (const frame of frames) {
    mark(frame);
    frame.addEventListener('scroll', onScroll, { passive: true });
    sizes?.observe(frame);
    if (frame.firstElementChild) sizes?.observe(frame.firstElementChild);
  }
  return () => {
    sizes?.disconnect();
    for (const frame of frames) frame.removeEventListener('scroll', onScroll);
  };
}
